import { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Grid } from "@mui/material"
import { Button, Pagination, Snackbar } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { DecisionLoadingCard } from "../components/cards/DecisionLoadingCard"
import { KonanPageHeader } from "../components/core/KonanPageHeader"
import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { CopyComponentsFlow } from "../features/ImportFlow/CopyComponentsFlow"
import { TaglistCard } from "../features/Taglist/components/TaglistCard"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { PaginatedTagListListList } from "../types/generated/api/PaginatedTagListListList"
import { useIsPermitted } from "../utils/PermissionsHelpers"

export function Taglist(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("taglists") // page-only; do not reference cards.json

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canAddTaglist = useIsPermitted("Add taglist", permissions.konan, flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve taglist", permissions.konan, flattenedKonanPermissions)

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const pageSize = 3

  // Pagination
  const [page, setPage] = useState<number>(0)

  const [duplicatedId, setDuplicatedId] = useState<string | null>(null)

  const [showTaglistCard, setShowTaglistCard] = useState<boolean>(false)

  //SSF States
  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<SortKey>("Most Recent")

  // SSF Types And Constants
  type SortKey = keyof typeof SORT_OPTIONS
  const SORT_OPTIONS = {
    "Most Recent": "-created_at",
    "Last Used": "-last_used",
  } as const

  // fetch taglists
  const { isLoading: isTaglistsLoading, data: taglists } = useQuery<
    AxiosResponse<PaginatedTagListListList>,
    AxiosError
  >(
    ["taglists", projectId, page, pageSize, searchValue, sortValue],
    () => KonanAPI.fetchTagLists(projectId as string, page + 1, pageSize, searchValue, SORT_OPTIONS[sortValue]),
    {
      enabled: !!projectId,
    },
  )

  const duplicateTaglist = (uuid: string): void => {
    setDuplicatedId(uuid)
    // scroll to top
    window.scrollTo(0, 0)
  }

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12} textAlign={"right"}>
        <CopyComponentsFlow<"Taglist">
          key={isDialogOpen.toString()}
          componentName="Taglist"
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
        />
        <KonanPageHeader title={t("pageTitle")} />
        <Grid mt={2}>
          <KonanSubHeader
            containerType="Taglist"
            sortValue={sortValue}
            setSortValue={(value) => {
              setPage(() => 0)
              setSortValue(() => value as SortKey)
            }}
            sortOptions={[
              { label: t("subheader.sortOptions.mostRecent"), value: "Most Recent" },
              { label: t("subheader.sortOptions.lastUsed"), value: "Last Used" },
            ]}
            searchValue={searchValue}
            setSearchValue={(value) => {
              setPage(() => 0)
              setSearchValue(() => value as string)
            }}
            isDebouncedInput
            actions={
              canShowDetails && canAddTaglist
                ? [
                    <Button
                      key="Import"
                      variant="secondary"
                      onClick={setIsDialogOpen.bind(null, true)}
                      disabled={showTaglistCard || isTaglistsLoading || !!duplicatedId}
                      size="regular"
                    >
                      {t("subheader.actions.import")}
                    </Button>,
                    <Button
                      key="Create_taglist"
                      variant="primary"
                      startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                      onClick={() => setShowTaglistCard(true)}
                      disabled={showTaglistCard || isTaglistsLoading || !!duplicatedId}
                      size="regular"
                    >
                      {t("subheader.actions.createTaglist")}
                    </Button>,
                  ]
                : undefined
            }
          />
        </Grid>
      </Grid>

      {!canShowDetails && (
        <Grid item flex={1}>
          <Snackbar
            fullWidth
            severity="info"
            title={t("cannotShowDetailsSnackBar.title")}
            description={t("cannotShowDetailsSnackBar.description")}
          />
        </Grid>
      )}

      {/* Taglists search empty state */}
      {!!projectId && !isTaglistsLoading && !taglists?.data.count && !showTaglistCard && searchValue && (
        <KonanEmptyState
          title={t("emptyState.noResults.title", { searchValue })}
          subTitle={t("emptyState.noResults.subtitle")}
        />
      )}

      {/* Taglists empty state */}
      {!!projectId && !isTaglistsLoading && !taglists?.data.count && !showTaglistCard && !searchValue && (
        <Grid item xs={12} className={"empty-container"}>
          <KonanEmptyState
            title={t("emptyState.noTaglists.title")}
            subTitle={t("emptyState.noTaglists.subtitle")}
            buttonText={canAddTaglist ? t("emptyState.noTaglists.buttonText") : undefined}
            setAction={setShowTaglistCard}
          />
        </Grid>
      )}

      {!isTaglistsLoading && (!!duplicatedId || showTaglistCard) && (
        <Grid item xs={12} key={!!duplicatedId ? `duplicate-${duplicatedId}` : "new"}>
          <TaglistCard
            uuid={duplicatedId ?? "new"}
            isDuplicate={!!duplicatedId}
            canShowDetails={canShowDetails}
            closeCreateCard={!!duplicatedId ? () => setDuplicatedId(null) : () => setShowTaglistCard(false)}
          />
        </Grid>
      )}

      {/* Loading state */}
      {isTaglistsLoading
        ? [1, 2].map((item: number) => <DecisionLoadingCard key={item} />)
        : taglists?.data.results?.map((taglist) => (
            <Grid item xs={12} key={taglist.uuid}>
              <TaglistCard
                duplicateTaglist={duplicateTaglist}
                uuid={taglist.uuid}
                disableDuplicateButton={!!duplicatedId || showTaglistCard}
                canShowDetails={canShowDetails}
                metaData={taglist}
              />
            </Grid>
          ))}

      {/* Pagination Section */}
      {!isTaglistsLoading && taglists?.data.results && taglists?.data.results.length > 0 && (
        <Grid item>
          <Pagination
            page={page + 1}
            pageSize={pageSize}
            totalCount={taglists.data.count as number}
            onPageChange={(page: number) => {
              setPage(page - 1)
              window.scrollTo(0, 0)
            }}
            variant={"complex"}
            hidePageSize={true}
          />
        </Grid>
      )}
    </Grid>
  )
}
